import type { Middleware } from "redux";
import type {
  FilterState,
  PriceRange,
  SearchDetails,
  MapState,
} from "@b2bportal/core-types";
import {
  type Amenity,
  type HotelStarRating,
  LodgingSelectionEnum,
  type MealPlanKind,
} from "@b2bportal/lodging-api";
import type { HotelSortOptionEnum } from "@overrides/types";
import {
  HOTELS_AVAILABILITY_PATH,
  navigateTo,
  HotelNavigationUtility,
  HOTEL_URL_PARAM_KEYS,
  toStarRating,
} from "@overrides/utilities";
import {
  type HotelsAvailabilityActionTypes,
  HotelsAvailabilityActions,
  type HotelsAvailabilitySearchState,
  initializeSearchState,
} from "../features/availability/store";

// Type for URL Param Handler
type UrlParamHandler<T> = (payload: T) => {
  updatedUrl: URL;
  navigateWithReplace: boolean;
};

type ActionTypes = HotelsAvailabilityActionTypes;

type HandlerMap = {
  [K in ActionTypes["type"]]?: UrlParamHandler<
    (ActionTypes & { type: K })["payload"]
  >;
};

const updateSearchParam = (
  updatedUrl: URL,
  key: string,
  value: string | null
) => {
  if (value !== null) {
    updatedUrl.searchParams.set(key, value);
  } else {
    updatedUrl.searchParams.delete(key);
  }
};

const handleSearchUpdate = ({
  currentUrl,
  search,
  replace,
}: {
  currentUrl: URL;
  search: SearchDetails;
  replace: boolean;
}) => {
  const { place, searchType, dateRange, guests, lodgingSelection, rooms } =
    search;
  const updatedUrl = new URL(currentUrl);

  // Keep trailing slash if it exits on the current url.
  const hasTrailingSlash = currentUrl.pathname.endsWith("/");

  if (
    place.id != null &&
    place.id.Id === "Lodgings" &&
    place.id.lodgingSelection.LodgingSelection === "Place"
  ) {
    // If this is a lodgingSelection based on a Google PlaceID, we'll use the `searchTerm` field to build the url.
    // This works around issues when `destructureLabel` is used in lodging-autocomplete where the sublabel will
    // flip-flop if we re-use it as the search term.
    updatedUrl.pathname = `${HOTELS_AVAILABILITY_PATH}/${HotelNavigationUtility.placeLabelToUrl(
      place.id.lodgingSelection.searchTerm
    )}${hasTrailingSlash ? "/" : ""}`;
  } else {
    updatedUrl.pathname = `${HOTELS_AVAILABILITY_PATH}/${HotelNavigationUtility.placeLabelAndSublabelToUrl(
      place.label,
      place.subLabel
    )}${hasTrailingSlash ? "/" : ""}`;
  }

  if (searchType !== LodgingSelectionEnum.Location) {
    updatedUrl.searchParams.delete(HOTEL_URL_PARAM_KEYS.LAT_LNG);
  }

  updateSearchParam(
    updatedUrl,
    HOTEL_URL_PARAM_KEYS.FROM_DATE,
    HotelNavigationUtility.formatDateForUrl(dateRange.from)
  );
  updateSearchParam(
    updatedUrl,
    HOTEL_URL_PARAM_KEYS.UNTIL_DATE,
    HotelNavigationUtility.formatDateForUrl(dateRange.until)
  );

  updateSearchParam(
    updatedUrl,
    HOTEL_URL_PARAM_KEYS.ADULTS_COUNT,
    guests.adults.toString()
  );

  updateSearchParam(
    updatedUrl,
    HOTEL_URL_PARAM_KEYS.ROOMS_COUNT,
    rooms.toString()
  );

  if (lodgingSelection != null) {
    updateSearchParam(
      updatedUrl,
      HOTEL_URL_PARAM_KEYS.LODGING_SELECTION,
      encodeURIComponent(JSON.stringify(lodgingSelection))
    );
  }

  if (guests.children.length > 0) {
    updateSearchParam(
      updatedUrl,
      HOTEL_URL_PARAM_KEYS.CHILDREN_COUNT,
      guests.children.length.toString()
    );
    updatedUrl.searchParams.delete(HOTEL_URL_PARAM_KEYS.CHILDREN_AGES);
    guests.children.forEach((age) => {
      updatedUrl.searchParams.append(
        HOTEL_URL_PARAM_KEYS.CHILDREN_AGES,
        age.toString()
      );
    });
  } else {
    updateSearchParam(updatedUrl, HOTEL_URL_PARAM_KEYS.CHILDREN_COUNT, null);
    updatedUrl.searchParams.delete(HOTEL_URL_PARAM_KEYS.CHILDREN_AGES);
  }

  return {
    updatedUrl,
    navigateWithReplace: replace,
  };
};

// Factory function to create the middleware with custom handlers
export const createHotelUrlMiddleware =
  (customHandlers: HandlerMap = {}): Middleware =>
  (_store) =>
  (next) =>
  (action: ActionTypes) => {
    // Skip URL handling in server environment
    if (typeof window === "undefined") {
      return next(action);
    }

    const currentUrl = new URL(window.location.toString());

    // Default handlers for each action
    const defaultHandlers: HandlerMap = {
      [initializeSearchState.fulfilled.type]: (
        payload: HotelsAvailabilitySearchState
      ) => {
        if (payload.requestedSearch != null)
          return handleSearchUpdate({
            currentUrl,
            search: payload.requestedSearch,
            replace: true,
          });

        return {
          currentUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.setRequestedSearch.type]: (
        payload: SearchDetails
      ) => {
        return handleSearchUpdate({
          currentUrl,
          search: payload,
          replace: false,
        });
      },
      [HotelsAvailabilityActions.setPriceFilter.type]: (
        payload: PriceRange
      ) => {
        const updatedUrl = new URL(currentUrl);

        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.PRICE_RANGE_MIN,
          payload.min.toString()
        );
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.PRICE_RANGE_MAX,
          payload.max.toString()
        );

        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.setAmenitiesFilter.type]: (
        payload: Amenity[]
      ) => {
        const updatedUrl = new URL(currentUrl);
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.AMENITIES,
          payload.length ? payload.join(",") : null
        );
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.setStarRatingFilter.type]: (
        payload: HotelStarRating[]
      ) => {
        const updatedUrl = new URL(currentUrl);
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.STAR_RATING,
          payload.length ? payload.map(toStarRating).join(",") : null
        );
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.setSort.type]: (
        payload: HotelSortOptionEnum
      ) => {
        const updatedUrl = new URL(currentUrl);
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.SORT_BY,
          HotelNavigationUtility.isValidSort(payload) ? payload : null
        );
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.setCancellationFilter.type]: (
        payload: boolean
      ) => {
        const updatedUrl = new URL(currentUrl);
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.FREE_CANCELLATION,
          payload ? "true" : null
        );
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.setPayLaterFilter.type]: (
        payload: boolean
      ) => {
        const updatedUrl = new URL(currentUrl);
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.PAY_LATER,
          payload ? "true" : null
        );
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.setMealPlanFilter.type]: (
        payload: MealPlanKind[]
      ) => {
        const updatedUrl = new URL(currentUrl);
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.MEAL_PLAN,
          payload.length ? payload.join(",") : null
        );
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.resetFilters.type]: (_: undefined) => {
        const updatedUrl = new URL(currentUrl);
        [
          HOTEL_URL_PARAM_KEYS.STAR_RATING,
          HOTEL_URL_PARAM_KEYS.AMENITIES,
          HOTEL_URL_PARAM_KEYS.PRICE_RANGE_MIN,
          HOTEL_URL_PARAM_KEYS.PRICE_RANGE_MAX,
          HOTEL_URL_PARAM_KEYS.FREE_CANCELLATION,
          HOTEL_URL_PARAM_KEYS.PAY_LATER,
          HOTEL_URL_PARAM_KEYS.MEAL_PLAN,
        ].forEach((param) => updatedUrl.searchParams.delete(param));
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.setAllFilters.type]: (
        payload: FilterState
      ) => {
        const updatedUrl = new URL(currentUrl);
        const {
          starRating,
          amenities,
          priceRange,
          freeCancellation,
          payLater,
          mealPlan,
          userRating,
        } = payload;
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.STAR_RATING,
          starRating.length ? starRating.map(toStarRating).join(",") : null
        );
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.AMENITIES,
          amenities.length ? amenities.join(",") : null
        );
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.PRICE_RANGE_MIN,
          priceRange.min.toString()
        );
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.PRICE_RANGE_MAX,
          priceRange.max.toString()
        );
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.FREE_CANCELLATION,
          freeCancellation ? "true" : null
        );
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.PAY_LATER,
          payLater ? "true" : null
        );
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.MEAL_PLAN,
          mealPlan.length ? mealPlan.join(",") : null
        );
        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.USER_RATING,
          typeof userRating === "number" ? userRating.toString() : null
        );
        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
      [HotelsAvailabilityActions.setMapData.type]: (payload: MapState) => {
        const updatedUrl = new URL(currentUrl);
        const { centroid, zoom } = payload;

        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.LAT_LNG,
          centroid ? `${centroid.lat},${centroid.lng}` : null
        );

        updateSearchParam(
          updatedUrl,
          HOTEL_URL_PARAM_KEYS.ZOOM,
          zoom ? zoom.toString() : null
        );

        return {
          updatedUrl,
          navigateWithReplace: true,
        };
      },
    };

    const mergedHandlers = {
      ...defaultHandlers,
      ...customHandlers,
    };

    const handler = mergedHandlers[action.type] as
      | UrlParamHandler<any>
      | undefined;

    if (handler != null) {
      const { updatedUrl, navigateWithReplace } = handler(action.payload);
      if (updatedUrl.toString() !== currentUrl.toString()) {
        navigateTo(
          `${updatedUrl.pathname}${updatedUrl.search}${updatedUrl.hash}`,
          {
            replace: navigateWithReplace,
          }
        );
      }
    }
    return next(action);
  };
